/**
 * UI System Design Tokens
 *
 * Central source of truth for all design tokens used in the Royale UI System
 */

// Colors
export const COLORS = {
  background: '#FFFFFF', // App background — referenced by 6+ files
  // Primary Colors
  LIGHT_GRAY: '#F7F7F9', // Form backgrounds, inactive buttons, stroke
  MEDIUM_GRAY: '#C2C2C8', // Placeholder text and images, disabled elements
  MEDIUM_DARK_GRAY: '#DBDCDE', // Selected icons, standard buttons
  DARK_GRAY: '#6D7074', // Regular text, icons, header text, back icon
  DISABLED_GRAY: '#ECE9E9', // Disabled forms

  PRIMARY: '#6200ea', // Primary action buttons
  PRIMARY_BLUE: '#007AFF', // Entry button, link color for clickable text
  MEDIUM_BLUE: '#3899F3', // Send contact button, invite CTA accent
  CHAT_SENT: '#007AFF', // Sent chat bubbles + send button — change this to retheme sent messages
  RECEIVED_GRAY: '#EDEDF0', // Received chat bubbles
  CHAT_TEXT_DARK: '#1C1C1E', // Chat text on light backgrounds

  WHITE: '#FFFFFF',
};

// Typography
export const TYPOGRAPHY = {
  HEADER_LARGE: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: COLORS.DARK_GRAY,
  },
  HEADER_MEDIUM: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: COLORS.DARK_GRAY,
  },
  HEADER_SMALL: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: COLORS.DARK_GRAY,
  },
  TEXT_LARGE: {
    fontSize: '24px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
  },
  BODY_REGULAR: {
    fontSize: '20px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
  },
  BODY_LARGE: {
    fontSize: '22px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
  },
  BODY_LIGHT: {
    fontSize: '20px',
    fontWeight: 'normal',
    color: COLORS.MEDIUM_GRAY,
  },
  BODY_SMALL: {
    fontSize: '16px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
  },
  BUTTON_REGULAR: {
    fontSize: '16px',
    fontWeight: 'normal',
    color: COLORS.LIGHT_GRAY,
  },
  BUTTON_LARGE: {
    fontSize: '24px',
    fontWeight: 'normal',
    color: COLORS.LIGHT_GRAY,
  },
  BUTTON_DISABLED: {
    fontSize: '16px',
    fontWeight: '300', // Light weight
    color: COLORS.LIGHT_GRAY,
  },
  CAPTION_TEXT: {
    fontSize: '12px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
  },
};

// Font Family
export const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen';

// Spacing
export const SPACING = {
  XS: '14px', // Spacing between label and input field
  SM: '8px', // Default spacing between related elements
  MD: '16px', // Standard padding, margins between sections
  LG: '24px', // Generous spacing between major components
  XL: '32px', // Maximum spacing, page margins
};

// Border Radius — named by usage, not size
export const BORDER_RADIUS = {
  BUBBLE: '18px', // Chat bubbles
  PILL: '24px', // Buttons, inputs, overlays
  CARD: '28px', // Cards, promotional overlays
  DETAIL: '32px', // Detail page surfaces
  CIRCLE: '50%', // Circular buttons
  // Legacy aliases — migrate consumers then remove
  NONE: '0px',
  SM: '4px',
  MD: '8px',
  LG: '16px',
  FULL: '9999px',
};

// Shadows — four-tier elevation system
export const SHADOWS = {
  TIER1: '0 1px 2px rgba(0,0,0,0.08)', // Subtle lift (circle buttons, system messages)
  TIER2: '0 2px 8px rgba(0,0,0,0.10)', // Card/button (action buttons, back button)
  TIER3: '0 -4px 20px rgba(0,0,0,0.12)', // Overlay edge (bottom sheets)
  TIER3_UP: '0 4px 20px rgba(0,0,0,0.12)', // Same depth as TIER3, positive Y (dropdowns, modals floating down)
  TIER4: '0 8px 32px rgba(0,0,0,0.15)', // Prominent float (modals)
};

// Transitions
export const TRANSITIONS = {
  FAST: '100ms', // Micro-interactions
  NORMAL: '200ms', // Standard transitions
  SLOW: '300ms', // Emphasis transitions
};

// Semantic Tokens — component-level tokens referencing primitives above
export const BUTTON_TOKENS = {
  ACTION_BG_PRIMARY: '#000000',
  ACTION_BG_SECONDARY: COLORS.WHITE,
  ACTION_TEXT_PRIMARY: COLORS.WHITE,
  ACTION_TEXT_SECONDARY: '#6B6B6B',
  ACTION_BG_DISABLED: '#E5E5E5',
  ACTION_TEXT_DISABLED: '#9B9B9B',
  ACTION_SHADOW: SHADOWS.TIER2,
  ACTION_RADIUS: BORDER_RADIUS.PILL,
  CIRCLE_BG: 'rgba(255,255,255,0.95)',
  CIRCLE_SHADOW: SHADOWS.TIER1,
  CIRCLE_ICON: COLORS.DARK_GRAY,
  BACK_BG: COLORS.WHITE,
  BACK_SHADOW: SHADOWS.TIER2,
  BACK_ICON: '#555555',
  SEND_BG: COLORS.CHAT_SENT,
};

export const INPUT_TOKENS = {
  PILL_BORDER: `1px solid ${COLORS.MEDIUM_DARK_GRAY}`,
  PILL_BG: COLORS.WHITE,
  PILL_BG_SECONDARY: '#F5F5F5',
  PILL_RADIUS: BORDER_RADIUS.PILL,
  PILL_SHADOW: '0 0 12px rgba(180,180,180,0.25)',
  TEXT_COLOR: COLORS.DARK_GRAY,
  PLACEHOLDER_COLOR: COLORS.MEDIUM_GRAY,
  NOTE_SHADOW: '0 0 12px rgba(180,180,180,0.25)', // Same as PILL_SHADOW — shared diffused glow
  NOTE_RADIUS: BORDER_RADIUS.PILL,
  CONTACT_BORDER: '0.5px solid #E0E0E0',
  CONTACT_RADIUS: BORDER_RADIUS.PILL,
  // Detail-page contact rows (2026-08-18 Detail frames). A row whose value the
  // peer owns — "claimed", the 2026-07-18 ruling — is a flat grey pill; a row
  // the viewer can edit keeps the white + hairline treatment. The locked fill is
  // measured off anon-profile_3.png and lands exactly on LIGHT_GRAY.
  CONTACT_BG_LOCKED: COLORS.LIGHT_GRAY,
  CONTACT_BG_EDITABLE: COLORS.WHITE,
  // Transparent rather than absent so locked and editable rows are the same height.
  CONTACT_BORDER_LOCKED: '0.5px solid transparent',
};

/**
 * Detail-page column widths (user ruling 2026-08-19).
 *
 * Two sizes, deliberately: the contact rows are narrower than the section
 * cards, which separates *their information* — actionable, tappable, the reason
 * the page exists — from *context about them*, which is read-only prose.
 *
 * The relationship is a PERCENTAGE and the cap is a max-width, because fixed
 * widths collapse the distinction exactly where space is tightest: on a 320pt
 * SE the page's padding leaves 272px, so a 320 card and a fixed-280 row would
 * both render at 272 and the two sizes would become one. At 87.5% the ratio
 * holds at every width (280/320 on a modern phone, 238/272 on an SE).
 */
export const DETAIL_TOKENS = {
  // Cap, not a target. The detail column is already ~310px on a 390pt phone
  // once the shell's 16px and the content's 24px padding are taken out, so a
  // low cap wastes nothing there but throws away ~30px on a Pro Max. Set high
  // enough that handsets use whatever they have and only tablets are capped.
  SECTION_MAX_WIDTH: '400px',
  // Contact rows are a FIXED 280px — never narrower (user ruling, re-stated
  // 2026-08-20). Below that some handles cannot be read or edited. The old
  // 87.5% resolved to 278px inside this card, which is how it drifted under.
  ROW_WIDTH: '100%',
  ROW_MAX_WIDTH: '280px',
  ROW_MIN_WIDTH: '280px',
  // Hometown and the About-me fields share ONE wide column (user, 2026-08-20).
  // Not a style preference — "Long Island City, New York" does not fit the
  // contact-row width, and the free-text answers below have the same problem.
  //
  // Full width of the form's content box — the card and the form already carry
  // padding, so 100% is the wide column. The contact rows stay pinned at 280px,
  // so these come out wider. The pin no longer lines up with the contact icons
  // above; accepted by the user.
  ABOUT_ROW_WIDTH: '100%',
};

export const CHAT_TOKENS = {
  BUBBLE_BG_SENT: COLORS.CHAT_SENT,
  BUBBLE_BG_RECEIVED: COLORS.RECEIVED_GRAY,
  BUBBLE_TEXT_SENT: COLORS.WHITE,
  BUBBLE_TEXT_RECEIVED: COLORS.CHAT_TEXT_DARK,
  BUBBLE_RADIUS_SENT: '18px 18px 0 18px',
  BUBBLE_RADIUS_RECEIVED: '18px 18px 18px 0',
  BUBBLE_MAX_WIDTH: '70%',
  SYSTEM_BG: COLORS.WHITE,
  SYSTEM_SHADOW: SHADOWS.TIER1,
  SYSTEM_TEXT: COLORS.DARK_GRAY,
  CARD_SHADOW: '0 3px 6px rgba(0,0,0,0.16)', // Chat card elevation (ContactCard, NameCard, InviteCTA)
  // One speak-beat both directions (2026-07-27): the Profile's greeting→share
  // send gap AND the Anon's landing→chat entrance stagger. Two bubbles landing
  // at once read as a data dump; a half-second gap reads as a person speaking.
  MESSAGE_BEAT_MS: 500,
};

export const SURFACE_TOKENS = {
  HEADER_BG_FROSTED: 'rgba(255,255,255,0.85)',
  HEADER_BLUR: 'blur(20px)',
  HEADER_BG_SOLID: COLORS.WHITE,
  OVERLAY_BACKDROP: 'rgba(0,0,0,0.3)',
  OVERLAY_RADIUS: BORDER_RADIUS.PILL,
  OVERLAY_SHADOW: SHADOWS.TIER3,
  DETAIL_RADIUS: BORDER_RADIUS.DETAIL,
};

// Common styles for UI System screens
export const UI_SCREEN_STYLES = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: FONT_FAMILY,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    marginBottom: '16px',
  },
  backButton: {
    position: 'absolute',
    left: '8px', // Consistent 8px from left across all headers
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: COLORS.DARK_GRAY,
    cursor: 'pointer',
    padding: '0',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'normal',
    color: COLORS.DARK_GRAY,
    margin: 0,
  },
  divider: {
    height: '1px',
    backgroundColor: COLORS.MEDIUM_DARK_GRAY,
    width: '100%',
    marginBottom: '24px',
  },
  content: {
    backgroundColor: COLORS.WHITE,
    padding: '20px',
    borderRadius: BORDER_RADIUS.MD, // showcase only
    boxShadow: SHADOWS.TIER1, // showcase only
  },
};
